import React, { useCallback, useEffect, useRef } from 'react';
import { Button, Typography } from 'antd';
import { motion } from 'framer-motion';
import { useLiquidGlass } from '@/hooks';
import { FORM_GLASS_SETTINGS } from '@/constants';
import { nextStepBody } from './nextStepCopy';
import styles from './NextStepCard.module.scss';

const { Text } = Typography;

const CARD_GLASS_SETTINGS = { ...FORM_GLASS_SETTINGS, radius: 16 };

interface NextStepCardProps {
  variant: 'next' | 'final';
  nextQuestionNumber: number;
  totalQuestions: number;
  disabled: boolean;
  onClick: () => void;
  isVoiceMode?: boolean;
  onHeightChange?: (height: number) => void;
  isGuest?: boolean;
  /** The package's coachingEnabled — with it off there's no follow-up chat to point at. */
  coachingEnabled?: boolean;
  /** Whether Results will offer Try Again (guests get a single attempt). */
  canRetake?: boolean;
}

export const NextStepCard: React.FC<NextStepCardProps> = ({
  variant,
  nextQuestionNumber,
  totalQuestions,
  disabled,
  onClick,
  onHeightChange,
  isVoiceMode = false,
  isGuest = false,
  coachingEnabled = true,
  canRetake = true,
}) => {
  const body = nextStepBody(variant, { coachingEnabled, canRetake, isVoiceMode, isGuest });
  const glassRef = useLiquidGlass<HTMLDivElement>(CARD_GLASS_SETTINGS);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      glassRef(node);
      rootRef.current = node;
    },
    [glassRef]
  );

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !onHeightChange) return;
    const report = () => onHeightChange(el.offsetHeight);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, [onHeightChange]);

  return (
    <motion.div
      ref={setRefs}
      className={styles.card}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.2 }}
    >
      <div className={styles.copy}>
        {variant === 'final' ? (
          <>
            <Text className={styles.prompt}>Interview completed!</Text>
            <Text className={styles.body}>{body}</Text>
          </>
        ) : (
          <>
            <Text className={styles.prompt}>
              {nextQuestionNumber === totalQuestions
                ? 'Ready for final question?'
                : 'Ready for next question?'}
            </Text>
            <Text className={styles.body}>{body}</Text>
          </>
        )}
      </div>

      <Button type="text" className={styles.actionBtn} onClick={onClick} disabled={disabled}>
        {variant === 'final' ? (isGuest ? 'Finish Interview' : 'View Results') : 'Continue'}
      </Button>
    </motion.div>
  );
};
